/**
 * Iter 209 — Mobile Labour Performance (site supervisor, end-of-day entry).
 *
 * A supervisor picks their project + date, sees the labour assigned to that
 * project, taps each worker to record output / quality / rating / notes, and
 * hits "Save All". Each entry upserts /labour/{id}/daily-assignment (which the
 * backend uses to compute a daily performance_score from output + attendance).
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Activity, Star, ChevronDown, Loader2, Save, CheckCircle2, Search } from 'lucide-react';
import { apiGet, apiPost } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

const todayStr = () => new Date().toISOString().slice(0, 10);

export default function LabourPerformance() {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(localStorage.getItem('perf.project_id') || '');
  const [date, setDate] = useState(todayStr());
  const [labour, setLabour] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(null);   // labour_id currently open
  const [entries, setEntries] = useState({});       // labour_id -> { ...fields }
  const [query, setQuery] = useState('');

  useEffect(() => {
    apiGet('/projects').then((r) => {
      const list = Array.isArray(r) ? r : (r?.projects || []);
      setProjects(list);
      if (!projectId && list.length) setProjectId(list[0].id);
    }).catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => { if (projectId) localStorage.setItem('perf.project_id', projectId); }, [projectId]);

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    apiGet(`/labour-master?project_id=${encodeURIComponent(projectId)}`)
      .then((r) => setLabour(r?.items || []))
      .catch(() => toast.error('Could not load labour'))
      .finally(() => setLoading(false));
    setEntries({});
    setExpanded(null);
  }, [projectId, date]);

  const setField = (lid, key, value) =>
    setEntries((e) => ({ ...e, [lid]: { ...(e[lid] || {}), [key]: value } }));

  const toggle = async (lid) => {
    if (expanded === lid) { setExpanded(null); return; }
    setExpanded(lid);
    // Prefill from any existing assignment for this date (correction flow).
    if (!entries[lid]) {
      try {
        const r = await apiGet(`/labour/${lid}/assignments?date_from=${date}&date_to=${date}`);
        const ex = (r?.items || [])[0];
        if (ex) {
          setEntries((e) => ({ ...e, [lid]: {
            activities_completed: ex.activities_completed ?? '',
            activities_planned: ex.activities_planned ?? '',
            output_qty: ex.output_qty ?? '',
            output_unit: ex.output_unit ?? '',
            quality_issues: ex.quality_issues ?? '',
            supervisor_rating: ex.supervisor_rating ?? 0,
            supervisor_notes: ex.supervisor_notes ?? '',
          } }));
        }
      } catch { /* fresh entry */ }
    }
  };

  const num = (v) => (v === '' || v == null ? null : Number(v));

  const saveAll = async () => {
    const ids = Object.keys(entries).filter((lid) => {
      const e = entries[lid];
      return e && Object.values(e).some((v) => v !== '' && v != null && v !== 0);
    });
    if (ids.length === 0) { toast.error('Enter performance for at least one worker'); return; }
    setSaving(true);
    let ok = 0;
    for (const lid of ids) {
      const e = entries[lid];
      try {
        await apiPost(`/labour/${lid}/daily-assignment`, {
          date,
          project_id: projectId,
          supervisor_id: user?.user_id || user?.id || 'self',
          supervisor_name: user?.name || '',
          activities_completed: num(e.activities_completed),
          activities_planned: num(e.activities_planned),
          output_qty: num(e.output_qty),
          output_unit: e.output_unit || null,
          quality_issues: num(e.quality_issues),
          supervisor_rating: e.supervisor_rating || null,
          supervisor_notes: e.supervisor_notes || null,
        });
        ok += 1;
      } catch { /* keep going */ }
    }
    setSaving(false);
    if (ok > 0) toast.success(`Saved performance for ${ok} worker${ok === 1 ? '' : 's'}`);
    else toast.error('Could not save');
  };

  const q = query.trim().toLowerCase();
  const filtered = !q ? labour : labour.filter((l) => (l.name || '').toLowerCase().includes(q) || (l.trade || '').toLowerCase().includes(q));
  const enteredCount = Object.keys(entries).filter((lid) => {
    const e = entries[lid];
    return e && Object.values(e).some((v) => v !== '' && v != null && v !== 0);
  }).length;

  return (
    <div className="min-h-screen bg-[#FDFDFD]" data-testid="labour-performance-page">
      <header className="sticky top-0 z-20 bg-white border-b border-slate-200 px-3 py-3 flex items-center justify-between">
        <button onClick={() => navigate('/m')} className="flex items-center gap-1 text-xs font-display font-bold uppercase tracking-wider text-slate-600" data-testid="perf-back">
          <ArrowLeft size={14} /> Back
        </button>
        <h1 className="font-display font-bold text-sm uppercase tracking-wide flex items-center gap-1.5">
          <Activity size={15} /> Labour Performance
        </h1>
        <div className="w-12" />
      </header>

      <div className="p-3 space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] font-display font-bold tracking-wider uppercase text-slate-500">Project</label>
            <select value={projectId} onChange={(e) => setProjectId(e.target.value)}
              className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm mt-1 bg-white focus:outline-none focus:border-[#3b4872]"
              data-testid="perf-project">
              <option value="">— Select —</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-display font-bold tracking-wider uppercase text-slate-500">Date</label>
            <input type="date" value={date} max={todayStr()} onChange={(e) => setDate(e.target.value)}
              className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm mt-1 focus:outline-none focus:border-[#3b4872]"
              data-testid="perf-date" />
          </div>
        </div>

        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search worker…"
            className="w-full bg-white border border-slate-300 rounded-sm pl-8 pr-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]"
            data-testid="perf-search" />
        </div>

        {loading && <div className="flex justify-center py-8"><Loader2 size={24} className="animate-spin text-[#3b4872]" /></div>}
        {!loading && filtered.length === 0 && (
          <div className="bg-white border border-slate-200 rounded-sm p-6 text-center text-xs text-slate-500" data-testid="perf-empty">
            No labour assigned to this project.
          </div>
        )}

        <div className="space-y-2 pb-24">
          {filtered.map((l) => {
            const e = entries[l.id] || {};
            const isOpen = expanded === l.id;
            const hasEntry = Object.values(e).some((v) => v !== '' && v != null && v !== 0);
            return (
              <div key={l.id} className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid={`perf-row-${l.id}`}>
                <button onClick={() => toggle(l.id)} className="w-full flex items-center gap-3 p-3 text-left" data-testid={`perf-toggle-${l.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-sm truncate">{l.name}</div>
                    <div className="text-[11px] text-slate-500 truncate">{l.trade || '—'}</div>
                  </div>
                  {hasEntry && <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />}
                  <ChevronDown size={16} className={`text-slate-400 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>
                {isOpen && (
                  <div className="border-t border-slate-100 p-3 space-y-3 bg-slate-50/50" data-testid={`perf-form-${l.id}`}>
                    <div className="grid grid-cols-2 gap-2">
                      <NumField label="Activities done" value={e.activities_completed} onChange={(v) => setField(l.id, 'activities_completed', v)} testid={`perf-done-${l.id}`} />
                      <NumField label="Activities planned" value={e.activities_planned} onChange={(v) => setField(l.id, 'activities_planned', v)} testid={`perf-planned-${l.id}`} />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <NumField label="Output qty" value={e.output_qty} onChange={(v) => setField(l.id, 'output_qty', v)} testid={`perf-output-${l.id}`} />
                      <TxtField label="Unit (e.g. m³)" value={e.output_unit} onChange={(v) => setField(l.id, 'output_unit', v)} testid={`perf-unit-${l.id}`} />
                    </div>
                    <NumField label="Quality issues (count)" value={e.quality_issues} onChange={(v) => setField(l.id, 'quality_issues', v)} testid={`perf-quality-${l.id}`} />
                    <div>
                      <label className="text-[10px] font-display font-bold tracking-wider uppercase text-slate-500">Rating</label>
                      <div className="flex gap-1.5 mt-1" data-testid={`perf-rating-${l.id}`}>
                        {[1, 2, 3, 4, 5].map((n) => (
                          <button key={n} type="button" onClick={() => setField(l.id, 'supervisor_rating', n)}
                            className="p-1" data-testid={`perf-star-${l.id}-${n}`}>
                            <Star size={22} className={n <= (e.supervisor_rating || 0) ? 'text-amber-400 fill-amber-400' : 'text-slate-300'} />
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] font-display font-bold tracking-wider uppercase text-slate-500">Notes</label>
                      <textarea value={e.supervisor_notes || ''} onChange={(ev) => setField(l.id, 'supervisor_notes', ev.target.value)} rows={2}
                        className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm mt-1 focus:outline-none focus:border-[#3b4872]"
                        data-testid={`perf-notes-${l.id}`} />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Sticky Save All */}
      <div className="fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 p-3 z-30"
        style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}>
        <button onClick={saveAll} disabled={saving || enteredCount === 0}
          className="w-full bg-[#3b4872] text-white rounded-sm px-4 py-3 text-sm font-display font-bold uppercase tracking-wider hover:bg-[#2d3856] disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="perf-save-all">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Save All{enteredCount ? ` · ${enteredCount}` : ''}
        </button>
      </div>
    </div>
  );
}

function NumField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[10px] font-display font-bold tracking-wider uppercase text-slate-500">{label}</label>
      <input type="number" inputMode="decimal" value={value ?? ''} onChange={(e) => onChange(e.target.value)}
        className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm mt-1 focus:outline-none focus:border-[#3b4872]"
        data-testid={testid} />
    </div>
  );
}
function TxtField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[10px] font-display font-bold tracking-wider uppercase text-slate-500">{label}</label>
      <input type="text" value={value ?? ''} onChange={(e) => onChange(e.target.value)}
        className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm mt-1 focus:outline-none focus:border-[#3b4872]"
        data-testid={testid} />
    </div>
  );
}
